import { LinkUtils } from '../../Utils/LinkUtils';

/**
 * Map an explore object type to the web section route that renders it, or
 * `undefined` when the type has no in-app content page.
 *
 * Object types mirror the backend `ExploreEntry` @JsonSubTypes enum
 * (activityWorkout, article, workoutTemplate, meditation, breathing, webinar,
 * onlineMeeting, offlineMeeting, link). Every content view resolves the entry by
 * id via ExploreContentBuilder.findOne and renders by the entry's own type, so
 * the section only affects the URL/active nav tab. `link` entries open an
 * external URL and have no content page, so they return `undefined`.
 */
export function exploreTypeToWebSection(objectType: string): string | undefined {
  switch (objectType) {
    case 'meditation':
    case 'breathing':
      return 'mental-health';
    case 'workoutTemplate':
    case 'activityWorkout':
      return 'move';
    case 'article':
    case 'webinar':
    case 'onlineMeeting':
    case 'offlineMeeting':
      return 'academy';
    case 'link':
      return undefined;
    default:
      return 'academy';
  }
}

/**
 * Resolve a backend-provided entry link to the equivalent in-app web path, or
 * undefined when it can't be mapped to an internal route.
 *
 * Schedule entry `linkProps` are authored for the native app (`kinastic://`
 * deep links + native screen names), so `explore/<objectType>/<id>` targets are
 * translated to the section route the web app uses for that content type.
 * External web URLs return undefined here and are opened by the caller.
 */
export function resolveEntryWebPath(href?: string): string | undefined {
  if (!href) return undefined;

  let path: string | undefined;
  if (href.startsWith('kinastic://')) {
    path = `/${href.slice('kinastic://'.length)}`;
  } else {
    const parsed = LinkUtils.parseLink(href);
    if (LinkUtils.isInternalUrl(parsed)) {
      path = parsed.replace(/^https?:\/\/*[^/]+/, '');
    }
  }
  if (!path) return undefined;

  const segments = path.split('?')[0].split('/').filter(Boolean); // ['explore', '<type>', '<id>']
  if (segments[0] === 'explore' && segments.length >= 3) {
    const section = exploreTypeToWebSection(segments[1]);
    return section ? `/${section}/${segments[2]}` : undefined;
  }

  return path;
}
